import { Box } from "@mui/system";
import type { DateTime } from "luxon";
import { type Dispatch, type FC, type SetStateAction, useEffect, useMemo, useRef } from "react";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { NurseFullRole } from "../../../utils/models";
import { getShiftIndexByShiftTimes } from "../../../utils/utilFunctions";
import type { ShiftIndexEnum } from "../Clickable/DayNightSelector";
import { type RoleSource, ShiftHoursDisplayAndPicker } from "../Inputs/ShiftHoursDisplayAndPicker";

type ShiftWindow = { startTime: DateTime; endTime: DateTime };

type Props = {
	shiftIndex: ShiftIndexEnum;
	setShiftIndex: ((index: ShiftIndexEnum) => void) | Dispatch<SetStateAction<number>>;
	selectedRoleForShift: NurseFullRole | undefined;
	shiftStart: DateTime;
	shiftEnd: DateTime;
	setShiftStart: ((start: DateTime) => void) | Dispatch<SetStateAction<DateTime>>;
	setShiftEnd: ((end: DateTime) => void) | Dispatch<SetStateAction<DateTime>>;
	date: DateTime;
	roleSource: RoleSource;
	selectedLocation?: string;
	selectedRoleGroups?: string[];
	enabled?: boolean;
	lockedTimes?: ShiftWindow;
	/**
	 * Prefill for the times (e.g. the window of the gap being posted). Read once on mount — remount to
	 * prefill another window. Unlike `lockedTimes` the picker stays editable; the prefill holds until the
	 * role or the job changes, and a LOCATION change keeps it (choosing a unit only sets the unit). Seed the
	 * role/job in the same render as the prefill (initial state, not an effect), or seeding them counts as
	 * that change and releases it.
	 */
	initialTimes?: ShiftWindow;
	jobId?: string;
	/** Drop the picker's own "Choose Hours" caption, for a caller that labels the field itself (a sheet's field label). */
	hideLabel?: boolean;
	/** The time editor's look (`EditTimePopover`'s `appearance`): `touch` for a picker inside a phone sheet. Absent = today's editor. */
	editorAppearance?: "default" | "touch";
	/** Optional exclusive elapsed-duration limit passed through to the time editor. */
	maximumDurationHours?: number;
};

// The prefill and the role/job it was chosen for.
type InitialTimesHold = { times: ShiftWindow; role: NurseFullRole | undefined; jobId: string | undefined; applied: boolean };

/**
 * Component to display shift index(day/night) and shift hours with a picker.
 * @param shiftIndex - The index of the shift (day/night).
 * @param setShiftIndex - Function to set the shift index.
 * @param setShiftEnd
 * @param shiftStart
 * @param setShiftStart
 * @param shiftEnd
 * @param selectedRoleForShift - The role selected for the shift.
 * @param date - The date for which the shift is being set, used to calculate shift times.
 * @param roleSource - The source of the role, either "shift" or "role".
 * @param selectedLocation - Optional location for the shift, not used in this component but can be passed for consistency.
 * @param lockedTimes - When provided, the shift times are fixed to these values (no recalculation on role/location change) and the picker is disabled.
 * @param initialTimes - Optional prefill that wins over the role/location templates until the role or job changes (a location change keeps it); absent, the times start from the template as before.
 */
export const ShiftDetailedTimeViewWithPicker: FC<Props> = ({
	shiftIndex,
	setShiftIndex,
	setShiftEnd,
	shiftStart,
	setShiftStart,
	shiftEnd,
	selectedRoleForShift,
	date,
	roleSource,
	selectedLocation,
	selectedRoleGroups,
	enabled,
	lockedTimes,
	initialTimes,
	jobId,
	hideLabel,
	editorAppearance,
	maximumDurationHours,
}) => {
	const isSelfSetShiftIndex = useRef(false);
	const shiftIndexRef = useRef(shiftIndex);
	const initialTimesHold = useRef<InitialTimesHold | null>(initialTimes ? { times: initialTimes, role: selectedRoleForShift, jobId, applied: false } : null);
	const { calculateShiftTimes, getDayIndexByDate, getWeekIndexByDate, hospitalData, timezone } = useHospitalData();
	const dateWithCorrectTimezone = useMemo(() => date.startOf("day").setZone(timezone), [date, timezone]);

	useEffect(() => {
		shiftIndexRef.current = shiftIndex;
	}, [shiftIndex]);

	const shiftTimes = useMemo(() => {
		return calculateShiftTimes(
			hospitalData.weeks[getWeekIndexByDate(dateWithCorrectTimezone)].weeklyShifts[shiftIndex].days[getDayIndexByDate(dateWithCorrectTimezone)],
			shiftIndex,
			selectedRoleForShift,
			selectedLocation,
		);
	}, [
		calculateShiftTimes,
		dateWithCorrectTimezone,
		getDayIndexByDate,
		getWeekIndexByDate,
		hospitalData.weeks,
		selectedRoleForShift,
		shiftIndex,
		selectedLocation,
	]);

	useEffect(() => {
		if (lockedTimes) {
			setShiftStart(lockedTimes.startTime);
			setShiftEnd(lockedTimes.endTime);
			return;
		}
		const hold = initialTimesHold.current;
		// The job is watched by its own effect below; here the role is what can release the prefill.
		if (hold && hold.role === selectedRoleForShift) {
			/**
			 * An explicit prefill beats the template until the user changes WHAT is being posted — the role or
			 * the job. The mount-time calculation, a data refresh, the shift index following the prefill and a
			 * unit being picked all recalculate without the user asking about the hours, and none of them may
			 * overwrite it: a unit only says where, not when. The pending self-set flag is consumed here too —
			 * left set, it would swallow the first recalculation after release.
			 */
			isSelfSetShiftIndex.current = false;
			if (!hold.applied) {
				hold.applied = true;
				setShiftStart(hold.times.startTime);
				setShiftEnd(hold.times.endTime);
			}
			return;
		}
		// Role changed (or there was no prefill): from here on the templates drive the times.
		initialTimesHold.current = null;
		if (isSelfSetShiftIndex.current) {
			/**
			 * The times are updated because the shift index was changed - however if we changed the shift index from our useEffect below we should not update the times
			 */
			isSelfSetShiftIndex.current = false;
			return;
		}

		setShiftStart(shiftTimes.startTime);
		setShiftEnd(shiftTimes.endTime);
		// A location change reaches this through `shiftTimes` (its template is per location); it no longer releases a prefill.
	}, [shiftTimes, setShiftStart, setShiftEnd, lockedTimes, selectedRoleForShift]);

	/**
	 * A job change releases a held prefill to the template. Its own effect, because the templates do not depend on the
	 * job: without a prefill a job change has never moved the times, and folding `jobId` into the effect above would
	 * start overwriting times the user picked on every job change. With no prefill held this does nothing.
	 */
	useEffect(() => {
		const hold = initialTimesHold.current;
		if (lockedTimes || hold === null || hold.jobId === jobId) return;
		initialTimesHold.current = null;
		setShiftStart(shiftTimes.startTime);
		setShiftEnd(shiftTimes.endTime);
	}, [jobId, lockedTimes, shiftTimes, setShiftStart, setShiftEnd]);

	useEffect(() => {
		const shiftIndex = getShiftIndexByShiftTimes({
			startTime: shiftStart,
			endTime: shiftEnd,
		});
		if (shiftIndexRef.current === shiftIndex) {
			return;
		}
		isSelfSetShiftIndex.current = true;
		setShiftIndex(shiftIndex);
	}, [shiftStart, shiftEnd, setShiftIndex]);

	return (
		<Box
			sx={{
				width: "100%",
			}}
		>
			<ShiftHoursDisplayAndPicker
				selectedRoleForShift={selectedRoleForShift}
				setShiftEnd={setShiftEnd}
				setShiftStart={setShiftStart}
				shiftStart={shiftStart}
				shiftEnd={shiftEnd}
				roleSource={roleSource}
				selectedRoleGroups={selectedRoleGroups}
				enabled={lockedTimes ? false : enabled}
				jobId={jobId}
				hideLabel={hideLabel}
				editorAppearance={editorAppearance}
				maximumDurationHours={maximumDurationHours}
			/>
		</Box>
	);
};
